import React, { Fragment } from "react";
import { RouteComponentProps } from "react-router";
import { Row, message, Spin, Table, Tag } from "antd";
import Card from "../components/Card";
import { Audit } from "../helpers/shared-interfaces";
import { capitalizeFirstLetter, request } from "../helpers/utilities";
import { AxiosResponse } from "axios";
import { NavLink } from "react-router-dom";
import { GET_AUDIT_QUERY } from "../gql/general";
import moment from "moment";

interface ModelsInterface {
  [key: string]: string;
}

const MapSingularModelToPlural: ModelsInterface = {
  DEPARTMENT: "departments",
  AUDIT: "audits",
  USER: "users",
  ROLE: "roles",
  CUSTOMERSUBSCRIPTION: "customers",
};

interface AuditedChanges {
  [key: string]: string | string[];
}

interface State {
  isLoading: boolean;
  id: string | number;
  audit: Audit;
}

interface RouteParams {
  id: string;
}

export function injectKeyProp(data: Audit[]): Audit[] {
  const tempData: Audit[] = data.map((audit: Audit): Audit => {
    return { ...audit, key: audit.id };
  });
  return tempData;
}

export const mainDataColumns = [
  {
    title: "User",
    dataIndex: "user",
    key: "user",
    align: "center" as const,
    render(text: string, record: Audit): JSX.Element {
      if (!record.user) {
        return <span>System User</span>;
      } else {
        return <span>{record.user.name}</span>;
      }
    },
  },
  {
    title: "Action",
    dataIndex: "action",
    key: "actions",
    align: "center" as const,
    render(text: string, record: Audit): JSX.Element {
      return <span>{capitalizeFirstLetter(record.action)}</span>;
    },
  },
  {
    title: "Model",
    dataIndex: "model",
    key: "model",
    align: "center" as const,
    render(text: string, record: Audit): JSX.Element {
      return (
        <span>
          <Tag>{record.auditableType}</Tag>
        </span>
      );
    },
  },
  {
    title: "Resource Id",
    dataIndex: "resourceId",
    align: "center" as const,
    key: "resourceId",
    render(text: string, record: Audit): JSX.Element {
      const model = record.auditableType && record.auditableType.toUpperCase();
      const mapping = model && MapSingularModelToPlural[model];
      const resourceId =
        model === "CUSTOMERSUBSCRIPTION"
          ? record.customerId
          : record.auditableId;
      if (mapping && resourceId) {
        return (
          <NavLink to={`${mapping}/${resourceId}`}>
            {record.auditableId}
          </NavLink>
        );
      } else {
        return <span>{record.auditableId}</span>;
      }
    },
  },
  {
    title: "Date",
    dataIndex: "datetime",
    key: "datetime",
    align: "center" as const,
    render(text: string, record: Audit): JSX.Element {
      const formattedDateTime = moment(record.createdAt).format("llll");
      return <span>{formattedDateTime}</span>;
    }
  },
];

export const diffTableColumns = [
  {
    title: "Property Changed",
    dataIndex: "name",
    key: "PropertyChanged",
    render(text: string): JSX.Element {
      return <span>{text}</span>;
    },
  },
  {
    title: "Previous Value",
    dataIndex: "previous",
    key: "PreviousValue",
    render(text: string): JSX.Element {
      return <span>{text}</span>;
    },
  },
  {
    title: "Current Value",
    dataIndex: "next",
    key: "CurrentValue",
    render(text: string): JSX.Element {
      return <span>{text}</span>;
    },
  },
];

export function AuditTableFactory(data: Audit[]): JSX.Element {
  const tempData: Audit[] = data.map((auditLog: Audit): Audit => {
    return { ...auditLog, key: auditLog.id };
  });
  return (
    <Table
      pagination={false}
      bordered={true}
      columns={mainDataColumns}
      dataSource={tempData}
    />
  );
}

export function DiffTableCreator(audit: Audit): JSX.Element {
  const auditedChanges: AuditedChanges = audit.auditedChanges;
  const propsChanged = Object.keys(auditedChanges);
  const restructuredData = [];
  for (const propChanged of propsChanged) {
    const changedConfig = {
      name: propChanged,
      previous: auditedChanges[propChanged][0],
      next: auditedChanges[propChanged][1],
    };
    restructuredData.push(changedConfig);
  }
  return (
    <Table
      pagination={false}
      columns={diffTableColumns}
      dataSource={restructuredData}
      bordered={true}
    />
  );
}

export default class ViewAudit extends React.Component<
  RouteComponentProps<RouteParams>,
  State
> {
  constructor(props: Readonly<RouteComponentProps<RouteParams>>) {
    super(props);
    this.state = {
      isLoading: true,
      id: "",
      audit: {
        id: "",
        auditableId: "",
        auditableType: undefined,
        action: "",
        auditedChanges: { name: "" },
        createdAt: "",
      },
    };

    this.getAuditData = this.getAuditData.bind(this);
  }

  redirectToAllAuditsPage = (): void => {
    this.props.history.push("/audits");
  };

  getAuditData(): void {
    const { id } = this.state;
    this.setState(
      {
        isLoading: true,
      },
      (): void => {
        request({
          query: GET_AUDIT_QUERY,
          variables: { id },
        }).then(({ data }: AxiosResponse<any>): void => {
          this.setState({ isLoading: false });
          if (!data.errors) {
            this.setState({ audit: data.data.audit });
          } else {
            message.error("Failed to Get audit data");
          }
        });
      },
    );
  }

  componentDidMount(): void {
    this.setState({ id: this.props.match.params.id }, (): void => {
      this.getAuditData();
    });
  }

  render(): JSX.Element | null {
    const { isLoading, audit } = this.state;
    return (
      <Spin spinning={isLoading}>
        <Card>
          <Fragment>
            <Row
              style={{
                marginBottom: "24px",
              }}
            >
              {AuditTableFactory([audit])}
            </Row>
            {audit.action === "update" && <Row>{DiffTableCreator(audit)}</Row>}
          </Fragment>
        </Card>
      </Spin>
    );
  }
}
